import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { environment } from '../../../environments/environment';
import { MetricQuery, MetricQueryResult, MetricScope } from './metrics.models';
import { MetricSeries } from '@/shared/metric-chart/metric-chart.component';

/**
 * Client for the generic metrics endpoints. Power is the first metric type; room occupancy,
 * usage and other statistics use the same calls with a different `type`.
 */
@Injectable({ providedIn: 'root' })
export class CloudMetricsService {
    private http = inject(HttpClient);
    private cloudApi = inject(CloudApiService);

    query(scope: MetricScope, id: string, query: MetricQuery): Promise<MetricQueryResult> {
        let params = new HttpParams().set('type', query.type).set('from', query.from.toISOString()).set('to', query.to.toISOString());
        if (query.bucketMinutes) params = params.set('bucketMinutes', query.bucketMinutes);
        if (query.includeBreakdown === false) params = params.set('includeBreakdown', false);
        if (query.workingHours === false) params = params.set('workingHours', false);
        return firstValueFrom(this.http.get<MetricQueryResult>(`${environment.apiBaseUrl}/${scope}/${encodeURIComponent(id)}/metrics`, { headers: this.authHeaders(), params }));
    }

    getLocationMetrics(locationId: string, query: MetricQuery): Promise<MetricQueryResult> {
        return this.query('locations', locationId, query);
    }

    getDeviceMetrics(deviceId: string, query: MetricQuery): Promise<MetricQueryResult> {
        return this.query('devices', deviceId, query);
    }

    getLinkedDeviceMetrics(linkedDeviceId: string, query: MetricQuery): Promise<MetricQueryResult> {
        return this.query('linked-devices', linkedDeviceId, query);
    }

    private authHeaders(): HttpHeaders {
        const token = this.cloudApi.token();
        return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
    }
}

/** One chart series per source for a value key (e.g. avgWatts per PDU), in the order the API returns. */
export function sourceSeries(result: MetricQueryResult | null, valueKey: string): MetricSeries[] {
    return (result?.sources ?? []).map((s) => ({
        key: s.linkedDeviceId ?? s.sourceKey,
        label: s.sourceName || s.sourceKey,
        points: s.points.filter((p) => p.values[valueKey] != null).map((p) => ({ t: Date.parse(p.t), v: p.values[valueKey] }))
    }));
}
